// =============================================================================
// AnimatedGif — GIF that plays when on-screen and pauses (via static thumb)
// when scrolled out of view.
// =============================================================================
// Browsers can't natively pause a GIF. The trick: swap the <img>'s src
// between the animated original and a static frame depending on whether
// the element is in view. Bonus: we don't even fetch the original until
// we're close to the viewport, so scrolling past hundreds of GIFs in
// history doesn't burn bandwidth on all of them at once.
//
// Two modes for "static frame":
//   - thumbSrc provided (attachments): use the existing WebP thumbnail
//     the backend already generated. Preserves the first-frame look.
//   - no thumbSrc (embeds, raw GIF URLs): fall back to a 1x1 transparent
//     pixel. The GIF stops animating but goes blank — ugly but rare in
//     practice since attachments cover the common path.
//
// rootMargin of "300px" means we start loading the animated version
// when it's still ~300px above/below the viewport. This avoids the
// frame-flip being visible during normal scrolling — by the time the
// GIF is on-screen it's already animating.
// =============================================================================

import { useEffect, useRef, useState } from 'react';

const TRANSPARENT_PIXEL =
  'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';

interface Props {
  /** URL of the animated GIF. */
  src: string;
  /** Optional static still frame to show when out of view. If absent, the
   *  GIF goes blank (transparent) when not visible. */
  thumbSrc?: string | null;
  alt?: string;
  width?: number;
  height?: number;
  className?: string;
  draggable?: boolean;
  onClick?: () => void;
  /** Fixes layout shift while loading: we always render at this size. */
  style?: React.CSSProperties;
}

export function AnimatedGif({
  src,
  thumbSrc,
  alt = '',
  width,
  height,
  className,
  draggable,
  onClick,
  style,
}: Props) {
  const ref = useRef<HTMLImageElement | null>(null);
  // inView starts false so we don't fetch the GIF until we know it's near
  // the viewport. The IntersectionObserver flips it as soon as we mount.
  const [inView, setInView] = useState(false);
  // hasEverBeenInView tracks whether the GIF has ever been close enough
  // to the viewport that we kicked off the network fetch. Once true, we
  // can swap quickly between animated and static (no re-fetch) — we just
  // change the src attribute. Without this flag, leaving the viewport
  // would set src=thumb and the next visit would re-fetch the GIF.
  // Browsers DO cache the GIF after the first load, but explicitly using
  // the same src twice avoids any re-decode hitch.
  const hasLoadedRef = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (typeof IntersectionObserver === 'undefined') {
      // Old browser fallback: assume always in view, just play normally.
      setInView(true);
      hasLoadedRef.current = true;
      return;
    }
    const obs = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            hasLoadedRef.current = true;
            setInView(true);
          } else {
            setInView(false);
          }
        }
      },
      {
        // Start loading 300px before the GIF enters the viewport so the
        // animated swap is invisible during normal scroll.
        rootMargin: '300px 0px',
        threshold: 0,
      }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  // Choose what to render:
  //   - never been in view: render the static thumb (don't fetch the GIF
  //     yet). If no thumb, render a transparent placeholder of the right
  //     size — preserves layout, no bandwidth.
  //   - in view: render the animated GIF.
  //   - was in view but scrolled away: render the static thumb again so
  //     animation pauses. The browser still has the GIF cached so coming
  //     back into view is instant.
  const displaySrc = inView
    ? src
    : hasLoadedRef.current
    ? thumbSrc || TRANSPARENT_PIXEL
    : thumbSrc || TRANSPARENT_PIXEL;

  return (
    <img
      ref={ref}
      src={displaySrc}
      alt={alt}
      width={width}
      height={height}
      className={className}
      draggable={draggable}
      onClick={onClick}
      style={style}
      // GIFs are fetched eagerly once we decide they're in view; before
      // that we're rendering the thumb / placeholder, which is fine to
      // lazy-load. We don't set loading="lazy" because the IO already
      // gives us better control.
    />
  );
}

/** Detects a GIF by content-type or URL extension. */
export function isGifLike(opts: { contentType?: string; url?: string }): boolean {
  if (opts.contentType === 'image/gif') return true;
  if (opts.url) {
    try {
      // Strip query string for URL extension check (Tenor URLs have ?ck=…)
      const path = new URL(opts.url).pathname.toLowerCase();
      if (path.endsWith('.gif')) return true;
    } catch {
      // Not a parseable URL — fall through.
    }
  }
  return false;
}
